import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { SLOT_TYPE_LABELS } from "./doctorConstants";
import s from "../clinic.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";

const STEP_TIMES = [];
for (let h = 8; h < 20; h++) {
  for (const m of [0, 15, 30, 45]) {
    STEP_TIMES.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

const SLOT_TYPES = Object.keys(SLOT_TYPE_LABELS);

export default function AdminDoctorManualSlots({ doctorId, branchId, date, onDateChange }) {
  const toast = useToast();
  const [grid, setGrid] = useState({});
  const [duration, setDuration] = useState(30);
  const [activeTime, setActiveTime] = useState(null);

  useEffect(() => {
    if (!doctorId || !branchId || !date) return;
    clinicAPI
      .getDoctorManualSlots(doctorId, { date, branch_id: branchId })
      .then((r) => {
        const map = {};
        for (const sl of r.slots || []) {
          const t = String(sl.start_time).slice(0, 5);
          map[t] = {
            enabled: sl.is_available,
            slot_type: sl.slot_type || "in_person",
          };
        }
        setGrid(map);
      });
  }, [doctorId, branchId, date]);

  const toggle = (time) => {
    setGrid((prev) => {
      const cur = prev[time];
      if (!cur) {
        const next = {
          ...prev,
          [time]: { enabled: true, slot_type: "in_person" },
        };
        setActiveTime(time);
        return next;
      }
      if (cur.enabled) {
        const next = { ...prev };
        delete next[time];
        if (activeTime === time) setActiveTime(null);
        return next;
      }
      return { ...prev, [time]: { ...cur, enabled: true } };
    });
    setActiveTime(time);
  };

  const setType = (time, slot_type) => {
    setGrid((prev) => {
      const cur = prev[time];
      if (!cur?.enabled) return prev;
      return { ...prev, [time]: { ...cur, slot_type } };
    });
  };

  const save = async () => {
    const slots = Object.entries(grid)
      .filter(([, v]) => v.enabled)
      .map(([start_time, v]) => ({
        start_time,
        duration_minutes: duration,
        slot_type: v.slot_type,
        is_available: true,
      }));
    try {
      await clinicAPI.saveDoctorManualSlots(doctorId, {
        date,
        branch_id: branchId,
        slots,
      });
      toast.success(
        slots.length
          ? `Сохранено ${slots.length} ручных слотов`
          : "Ручные слоты сняты — снова автоматика",
      );
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const clear = () => {
    setGrid({});
    setActiveTime(null);
    clinicAPI
      .saveDoctorManualSlots(doctorId, { date, branch_id: branchId, slots: [] })
      .then(() => toast.success("Ручная сетка отключена"))
      .catch((e) => toast.error(e.response?.data?.error || "Ошибка"));
  };

  const activeCell = activeTime ? grid[activeTime] : null;

  return (
    <div className={s.docSection}>
      <h4 className={s.docSectionTitle}>Ручные слоты на дату</h4>
      <p className={s.docSectionHint}>
        Отметьте время — клиент увидит только эти слоты. Клик по ячейке
        включает/выключает; тип слота — ниже.
      </p>
      <Field label="Дата сетки">
        <Input type="date" value={date} onChange={(e) => onDateChange(e.target.value)} />
      </Field>
      <Field label="Длительность слота (мин)">
        <Input
          type="number"
          min={5}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
        />
      </Field>
      {activeCell?.enabled && (
        <Field label={`Тип слота ${activeTime}`}>
          <Select
            value={activeCell.slot_type}
            onChange={(e) => setType(activeTime, e.target.value)}
          >
            {SLOT_TYPES.map((t) => (
              <option key={t} value={t}>
                {SLOT_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <div className={s.manualSlotLegend}>
        {SLOT_TYPES.map((t) => (
          <span key={t} className={s.manualLegendItem}>
            <span className={`${s.manualLegendDot} ${s[`slotType_${t}`]}`} />
            {SLOT_TYPE_LABELS[t]}
          </span>
        ))}
      </div>
      <div className={s.manualSlotGrid}>
        {STEP_TIMES.map((time) => {
          const cell = grid[time];
          const on = cell?.enabled;
          const typeClass = on ? s[`manualSlotType_${cell.slot_type}`] : "";
          return (
            <button
              key={time}
              type="button"
              className={`${s.manualSlotCell} ${on ? s.manualSlotCellOn : ""} ${typeClass}`}
              onClick={() => toggle(time)}
              title={on ? SLOT_TYPE_LABELS[cell.slot_type] : "Добавить слот"}
            >
              {time}
            </button>
          );
        })}
      </div>
      <div className={s.scheduleActions}>
        <Button type="button" onClick={save}>
          Сохранить сетку
        </Button>
        <Button type="button" variant="secondary" onClick={clear}>
          Сбросить (авто)
        </Button>
      </div>
    </div>
  );
}
